// dependencies
import React, { useEffect, useState } from "react";
import { useLocation, useParams } from "react-router-dom";

import { faPlus } from "@fortawesome/pro-regular-svg-icons";

import { Pagination, Breadcrumbs as PyBreadcrumbs } from "@performyard/py-components";
import { ASYNC_TASK_NAMES } from "@performyard/service-contracts";

//components
import DownloadButtonSSE from "../../../components.smart/DownloadButtonSSE";
import Button from "../../../components/Button";
import PageHeading from "../../../components/PageHeading";
import ComponentLoader from "../../../components/Spinner/ComponentLoader";
import EmployeeGoalsTable from "./EmployeeGoalsTable";
import { EmployeeGoalsFilter } from "./Filters";
import Filters from "./Filters";
import ReadableGoalsFilterChips from "./ReadableFilter";

//services
import {
	useCompanyFunction,
	useCompensationCycleDetails,
	useCurrentUser,
	useRbacEmployeePermissions,
	useSearchService,
	useSearchServicePrefetch,
	useUser,
} from "../../../api/hooks";
import { useIsNewNavigation } from "../../../api/hooks/defs/navigation";
//hooks
import { useFeatureFlagQuery } from "../../../hooks/useFeatureFlag";
import { useUserAsyncTask } from "../../../hooks/useUserAsyncTask";

import { SERVICE_GOAL, getSearch } from "../../../api/services/defs/search-service";
import { Goal } from "../../../api/services/searchServiceResultTypes/goal";

import { buildCompensationBreadcrumbs, parseCompensationOrigin } from "../../../utils/compensationBreadcrumbs";
import {
	buildEmployeeDashboardBreadcrumbs,
	parseEmployeeDashboardOrigin,
} from "../../../utils/employeeDashboardBreadcrumbs";
//utils
import { getFiltersForSearchService, getUserGoalsNavigationLinks } from "./utils";

import InfoPageTemplate from "../../../pageTemplates/InfoPageTemplate";
import StandardToolbarActionRow from "../../../pageTemplates/InfoPageTemplate/StandardToolbarActionRow";
//pageTemplate
import useInfoPageTemplateQueryState from "../../../pageTemplates/InfoPageTemplate/useInfoPageTemplateQueryState";
import ErrorPage from "../../Error";
import { migrateFiltersToNewFormat } from "./Filters/migrateFilters";
//config
import config from "./config";

const EmployeeGoals = () => {
	const { data: me, isLoading } = useCurrentUser();
	const userId = me?.me?.authorization?.payload?.user_id || "";

	const params = useParams();
	const location = useLocation();
	const page = params?.page || "mygoals";
	const employeeID = params?.employeeID || userId || "";
	const isNewNav = useIsNewNavigation();
	const headerLabel = page === "teamGoals" ? "My Team's Goals" : "My Goals";
	const { data: employee, isLoading: isUserLoading } = useUser({ userID: employeeID, disabled: !employeeID });

	// When a user drills in from a compensation cycle or another employee's dashboard, root the
	// breadcrumb on that origin and retitle the page to the employee being viewed.
	const compOrigin = isNewNav ? parseCompensationOrigin(location.search) : null;
	const { data: compCycle } = useCompensationCycleDetails(compOrigin?.cycleId ?? "", {
		enabled: Boolean(compOrigin?.cycleId),
	});
	const goalsLeafLabel = page === "teamGoals" ? "Team Goals" : "Goals";
	const employeeGoalsHeader = `${employee?.name ?? ""}'s ${goalsLeafLabel}`;
	const compBreadcrumbItems =
		compOrigin && employee?.name && compCycle?.name
			? buildCompensationBreadcrumbs({
					from: compOrigin.from,
					cycleName: compCycle?.name ?? "",
					employeeName: employee?.name ?? "",
					employeeId: employeeID,
					leafLabel: goalsLeafLabel,
				})
			: null;
	const dashboardOrigin = isNewNav ? parseEmployeeDashboardOrigin(location.search, userId) : null;
	const dashboardBreadcrumbItems =
		dashboardOrigin && dashboardOrigin.employeeId === employeeID && employee?.name
			? buildEmployeeDashboardBreadcrumbs({
					from: dashboardOrigin.from,
					employeeName: employee.name,
					leafLabel: goalsLeafLabel,
				})
			: null;
	const originBreadcrumbItems = compBreadcrumbItems ?? dashboardBreadcrumbItems;
	const { data: rbac, isLoading: isRbacLoading } = useRbacEmployeePermissions(employeeID, "goal", !employeeID);

	const canUserCreateGoals = rbac?.includes("goals:createGoal");
	const canUserDownloadCSV = rbac?.includes("goals:exportGoalCsv");
	const { task: exportTask, triggerTask: triggerExport } = useUserAsyncTask(ASYNC_TASK_NAMES.EXPORT_GOALS_CSV);
	const { value: ffGoals } = useFeatureFlagQuery("ff-is-goals-enabled", false);
	const { companyFunction: goalsEnabled, isLoading: loadingGoalsFF } = useCompanyFunction("goals");

	const {
		pageNumber,
		setPageNumber,
		sort,
		setSort,
		filters: rawFilters,
		setFilters,
		searchValue,
		debouncedSearchValue,
		setDebouncedSearch,
	} = useInfoPageTemplateQueryState<EmployeeGoalsFilter>({
		defaultSortName: "end_date",
		defaultSortDirection: "desc",
		defaultPageNumber: 1,
	});

	/**
	 * Filter Migration:
	 * Employee Goals filter was updated to use new PY-Components, changing the filter format.
	 * Old URLs/bookmarks may contain old format filters (e.g., {status: [{value: "active", label: "Active"}]}).
	 * We migrate these to the new format (e.g., {status: ["active"]}) so old links continue to work.
	 */
	const filters = migrateFiltersToNewFormat(rawFilters || {});

	/**
	 * Save migrated filters back to URL if migration occurred.
	 * This updates the URL with the new format so future visits use the correct format.
	 * Only runs once on mount to avoid infinite loops.
	 */
	useEffect(() => {
		if (rawFilters && Object.keys(rawFilters).length > 0) {
			// Check if migration changed anything by comparing stringified versions
			// We need to normalize both for comparison
			const normalizeForComparison = (f: unknown): unknown => {
				if (!f || typeof f !== "object") return f;
				const normalized = { ...(f as Record<string, unknown>) };
				// Convert arrays with objects to comparable format
				if (Array.isArray(normalized.status)) {
					normalized.status = normalized.status.map((s: unknown) =>
						typeof s === "object" && s !== null && "value" in s ? (s as { value: string }).value : s
					);
				}
				return normalized;
			};
			const rawNormalized = normalizeForComparison(rawFilters);
			const migratedNormalized = normalizeForComparison(filters);
			const needsMigration = JSON.stringify(rawNormalized) !== JSON.stringify(migratedNormalized);
			if (needsMigration) {
				setFilters(filters);
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const { data, isLoading: isFetchingGoals } = useSearchService<Goal>({
		service: SERVICE_GOAL,
		filters: getFiltersForSearchService(filters, employeeID, page, employee?.directReports, debouncedSearchValue),
		skip: (pageNumber - 1) * config.pageSize,
		limit: config.pageSize,
		sort: sort,
		defaultErrorMessage: "There was an error getting Goals",
		disabled: isLoading || isUserLoading,
	});

	useSearchServicePrefetch<Goal>(
		{
			service: SERVICE_GOAL,
			filters: getFiltersForSearchService(
				filters,
				employeeID,
				page,
				employee?.directReports,
				debouncedSearchValue
			),
			skip: pageNumber * config.pageSize,
			limit: config.pageSize,
			sort: sort,
			defaultErrorMessage: "There was an error getting Goals",
			disabled: isLoading || isUserLoading,
		},
		3
	);

	useSearchServicePrefetch<Goal>(
		{
			service: SERVICE_GOAL,
			filters: getFiltersForSearchService(
				filters,
				employeeID,
				page,
				employee?.directReports,
				debouncedSearchValue
			),
			skip: (pageNumber - 2) * config.pageSize,
			limit: config.pageSize,
			sort: sort,
			defaultErrorMessage: "There was an error getting Goals",
			disabled: isLoading || isUserLoading,
		},
		pageNumber > 1 ? 1 : 0
	);

	const goals = data?.results || [];
	const totalGoalsCount = data?.total || 0;

	const [isFiltersPanelOpen, setIsFiltersPanelOpen] = useState(false);
	const additionalFiltersCount = Object.keys(filters)?.length;

	const toolbarActionRow = (
		<StandardToolbarActionRow
			searchInput={searchValue}
			onSearchInputChange={(value) => setDebouncedSearch(value)}
			onFilterButtonPress={() => {
				setIsFiltersPanelOpen(true);
			}}
			activeFiltersCount={additionalFiltersCount}
			searchPlaceholder="Search Goals..."
		/>
	);

	const drawer = (
		<Filters
			isOpen={isFiltersPanelOpen}
			onClose={() => setIsFiltersPanelOpen(false)}
			filters={filters}
			onApplyFilters={(newFilters) => {
				setFilters(newFilters);
			}}
		/>
	);

	const toolbarInformationRow = (
		<ReadableGoalsFilterChips filters={filters} setFilters={(newFilters) => setFilters(newFilters)} />
	);

	if (isLoading || isUserLoading || isRbacLoading || loadingGoalsFF) {
		return <ComponentLoader />;
	}

	if ((!ffGoals && !loadingGoalsFF) || (!loadingGoalsFF && goalsEnabled === "disabled")) {
		return <ErrorPage code={403} />;
	}

	const mainContent = (
		<div>
			<EmployeeGoalsTable
				goals={goals}
				isFetchingGoals={isFetchingGoals}
				handleColumnHeaderClick={(sortValue) => setSort(sortValue)}
				activeSort={sort.name}
				activeSortDirection={sort.direction}
				key={page}
			/>

			{totalGoalsCount > 0 && (
				<div
					data-testid="paginationContainer"
					className="mx-auto mt-5 flex w-full max-w-[400px] justify-center"
				>
					<Pagination
						currentPage={pageNumber}
						pageCount={Math.ceil(totalGoalsCount / config.pageSize)}
						onPageChange={(newPage: number) => setPageNumber(newPage)}
					/>
				</div>
			)}
		</div>
	);

	const goalWizardBaseUrl = "/goal/wizard/";
	const goalWizardUrlParams = `assignee=${employeeID}`;

	const requestGoalsCsv = async (): Promise<{ taskId: string }> => {
		const response = await getSearch(
			SERVICE_GOAL,
			getFiltersForSearchService(filters, employeeID, page, employee?.directReports, debouncedSearchValue),
			0,
			0,
			sort,
			true
		);

		if (!response?.taskId) {
			throw new Error("The goals CSV export did not start");
		}

		return { taskId: response.taskId };
	};

	const actionButtons = [
		canUserDownloadCSV && (
			<DownloadButtonSSE
				key="download-csv"
				task={exportTask}
				testid="downloadCSVButton"
				onClick={() => triggerExport(requestGoalsCsv)}
			>
				Download CSV
			</DownloadButtonSSE>
		),
		canUserCreateGoals && ffGoals && (
			<Button
				className="no-goal-tooltip"
				data-testid="createGoalButton"
				href={`${goalWizardBaseUrl}?${goalWizardUrlParams}`}
				color="green"
				icon={faPlus}
			>
				Create Goal
			</Button>
		),
	];

	return (
		<InfoPageTemplate
			employeeID={employeeID}
			actionButtons={actionButtons}
			toolbarActionRow={toolbarActionRow}
			toolbarInformationRow={toolbarInformationRow}
			drawer={drawer}
			mainContent={mainContent}
			pageTitle={
				isNewNav
					? originBreadcrumbItems
						? employeeGoalsHeader
						: headerLabel
					: employee?.name
						? `Goals - ${employee?.name} - PerformYard`
						: "Employee Goals"
			}
			pageHeading={
				isNewNav ? (
					originBreadcrumbItems ? (
						<div>
							<div className="pb-4">
								<PyBreadcrumbs items={originBreadcrumbItems} />
							</div>
							<PageHeading>{employeeGoalsHeader}</PageHeading>
						</div>
					) : (
						<PageHeading>{headerLabel}</PageHeading>
					)
				) : undefined
			}
			navigationLinks={getUserGoalsNavigationLinks(
				userId,
				employeeID,
				employee?.name,
				(employee?.directReports?.length || 0) > 0
			)}
			activeNavigationLinkKey={page}
		/>
	);
};

export default EmployeeGoals;
